import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { Constants, EntityType } from '../../constants.new';
import { TaxRatesPanelService } from './tax.rates.panel.service';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../Compliance/AgGrid';
import { Subscription } from 'rxjs';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { TaxAuthority, TaxRateArea } from './tax.rates.panel.model';
import { AddTaxEntityModalComponent, AddTaxEntityModalParams } from './add.tax.entity.modal.component';
import { RestrictService } from '../../Common/Permissions/restrict.service';
import { UpgradeNavigationServiceHandler } from '../../Common/Routing/upgrade-navigation-handler.service';
import { GridSource, TaxRateGridComponent } from '../../Tax-Rate/tax.rate.grid.component';

@Component({
    selector: 'tax-rates-panel',
    templateUrl: './tax.rates.panel.component.html',
    styleUrls: ['./tax.rates.panel.component.scss'],
    standalone: false,
})
export class TaxRatesPanelComponent extends TaxRateGridComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _constants: Constants,
        public taxRatesPanelService: TaxRatesPanelService,
        private modalService: WeissmanModalService,
        private readonly _weissmanModalService: WeissmanModalService,
        private restrictService: RestrictService,
        private readonly _upgradeNavigationServiceHandler: UpgradeNavigationServiceHandler,
    ) {
        super(taxRatesPanelService, restrictService, modalService);
    }

    @Input() collectorId: number;
    @Input() assessorId: number;
    @Input() annualRateIncrease: number;
    @Output() maximizeToggled: EventEmitter<boolean> = new EventEmitter<boolean>();

    showBody: boolean = false;
    showDisabled: boolean = false;
    isMaximized: boolean = false;
    useTrueRateSystem: boolean = true;

    gridId: System.Guid;
    gridOptions = new AgGridOptionsBuilder({
        suppressScrollOnNewData: true,
        rowClassRules: {
            disabled: (params) => params.data && params.data.disabled,
        },
        onRowClicked: (params) => {
            if (!params.data) {
                return;
            }

            if (this.selectedGridSourceOption === 'areas') {
                this.launchTaxRateAreaModal(params.data.taxRateAreaId, false);
            } else {
                this.launchTaxAuthorityModal(params.data.taxAuthorityId, false);
            }
        },
    })
        .withContext(this)
        .withLoadingOverlay()
        .withSort()
        .withColumnResize()
        .withTextSelection()
        .withVerticalScrollBar()
        .build();

    private _agGridReady: boolean = false;
    private _taxRatesPanelDataSub: Subscription;
    private readonly _TAX_AUTHORITIES_GRID_ID: System.Guid = '15707BBA-6A46-447C-8E2B-0194BC9F771A';
    private readonly _TAX_RATE_AREAS_GRID_ID: System.Guid = '5066F6A7-A2D5-44AF-8D50-195CF2DB814D';

    ngOnInit(): void {
        this.gridId = this._TAX_AUTHORITIES_GRID_ID;
        this._taxRatesPanelDataSub = this.taxRatesPanelService.taxRatesPanelData$.subscribe(() => {
            // initial load
            this._setRowData();
        });
    }

    ngOnDestroy(): void {
        this._taxRatesPanelDataSub.unsubscribe();
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        if (!this._agGridReady) {
            this.gridApi.setGridOption('columnDefs', this.getGridColumns());
        }
        this._agGridReady = true;

        this._setRowData();
    }

    selectedGridSourceChanged(gridSource: GridSource): void {
        this.gridId = gridSource === 'authorities' ? this._TAX_AUTHORITIES_GRID_ID : this._TAX_RATE_AREAS_GRID_ID;
        this.gridApi.setGridOption('rowData', []);
        this.gridApi.setGridOption('columnDefs', this.getGridColumns());
        this._setRowData();
    }

    toggleMaximize(isMaximized: boolean) {
        this.isMaximized = isMaximized;
        this.maximizeToggled.emit(this.isMaximized);
    }

    expandOrCollapse(): void {
        this.showBody = !this.showBody;

        if (this.showBody) {
            this.loadPanel();
        } else {
            this._agGridReady = false;
        }
    }

    showDisabledChanged(): void {
        this._setRowData();
    }

    async addTaxRateArea(): Promise<void> {
        const modalParams: AddTaxEntityModalParams = {
            assessorId: this.assessorId,
            collectorId: this.collectorId,
            entityTypeId: EntityType.TaxRateArea,
        };

        const taxRateAreaId = await this._weissmanModalService.showAsync(
            AddTaxEntityModalComponent,
            modalParams,
            'modal-md',
        );

        if (taxRateAreaId) {
            this.launchTaxRateAreaModal(taxRateAreaId, true);
        }
    }

    async addTaxAuthority(): Promise<void> {
        const modalParams: AddTaxEntityModalParams = {
            collectorId: this.collectorId,
            entityTypeId: EntityType.TaxAuthority,
            defaultAnnualRateIncrease: this.annualRateIncrease,
        };

        const taxAuthorityId = await this._weissmanModalService.showAsync(
            AddTaxEntityModalComponent,
            modalParams,
            'modal-md',
        );

        if (taxAuthorityId) {
            this.launchTaxAuthorityModal(taxAuthorityId, true);
        }
    }

    navigateToCommandCenter(): void {
        this._upgradeNavigationServiceHandler.go('taxRateCommandCenter', {});
    }

    protected loadPanel(): void {
        if (this.collectorId) {
            this.taxRatesPanelService.loadTaxRatesByCollector(this.collectorId);
            this.selectedGridSourceOption = 'authorities';
        } else if (this.assessorId) {
            this.taxRatesPanelService.loadTaxRatesByAssessor(this.assessorId);
            this.selectedGridSourceOption = 'areas';
        }
    }

    protected getSharedColumns(): ColDef[] {
        return [
            {
                colId: 'code',
                headerName: 'Code',
                field: 'code',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
            },
        ];
    }

    protected getAuthorityColumns(): ColDef[] {
        return [
            {
                colId: 'name',
                headerName: 'Authority Name',
                field: 'name',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
            },
            {
                colId: 'taxAuthorityCategoryId',
                headerName: 'Category',
                field: 'taxAuthorityCategoryId',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                valueGetter: (params) => {
                    const taxAuthority: TaxAuthority = params.data;

                    if (!taxAuthority || taxAuthority.taxAuthorityCategoryId == null) {
                        return '';
                    }
                    return this._constants.TaxAuthorityCategories[taxAuthority.taxAuthorityCategoryId].displayName;
                },
            },
            {
                colId: 'taxableAssessmentTypeId',
                headerName: 'Taxable Assessment',
                field: 'taxableAssessmentTypeId',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                valueGetter: (params) => {
                    const taxAuthority: TaxAuthority = params.data;

                    if (!taxAuthority || taxAuthority.taxableAssessmentTypeId == null) {
                        return '';
                    }

                    const displayValue =
                        this._constants.TaxableAssessmentTypesForTrueRate[taxAuthority.taxableAssessmentTypeId]
                            ?.displayName;

                    return displayValue || '';
                },
            },
            {
                colId: 'taxAuthorityExemptionType',
                headerName: 'Exemptions Honored',
                field: 'taxAuthorityExemptionType',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
                sortable: false,
                valueGetter: (params) => {
                    const taxAuthority: TaxAuthority = params.data;

                    if (!taxAuthority || taxAuthority.taxAuthorityExemptionTypeId === null) {
                        return '';
                    }

                    let displayText: any =
                        this._constants.TaxAuthorityExemptionTypes[taxAuthority.taxAuthorityExemptionTypeId]
                            .displayName;

                    if (
                        taxAuthority.taxAuthorityExemptionTypeId ===
                        Core.TaxAuthorityExemptionTypeEnum.CertainExemptions
                    ) {
                        displayText = taxAuthority.exemptionsHonored.join(', ');
                    }

                    return displayText;
                },
            },
        ];
    }

    protected getAreaColumns(): ColDef[] {
        return [
            {
                colId: 'name',
                headerName: 'Rate Area Name',
                field: 'name',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnMedWidth,
            },
        ];
    }

    private _setRowData(): void {
        if (!(this.gridApi && this.taxRatesPanelService.isInitialized)) {
            return;
        }

        this.gridApi.setGridOption('rowData', this._getRowData());
        this.gridApi.sizeColumnsToFit();
    }

    private _getRowData(): TaxRateArea[] | TaxAuthority[] {
        if (this.selectedGridSourceOption === 'areas') {
            if (this.showDisabled) {
                return this.taxRatesPanelService.allTaxRateAreas;
            } else {
                return this.taxRatesPanelService.enabledTaxRateAreas;
            }
        } else {
            if (this.showDisabled) {
                return this.taxRatesPanelService.allTaxAuthorities;
            } else {
                return this.taxRatesPanelService.enabledTaxAuthorities;
            }
        }
    }
}
